import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaUserCog }    from '@react-icons/all-files/fa/FaUserCog';
import { FaUsers }      from '@react-icons/all-files/fa/FaUsers';
import { FaCog }        from '@react-icons/all-files/fa/FaCog';
import { FaHome }       from '@react-icons/all-files/fa/FaHome';
import { FaPlus }       from '@react-icons/all-files/fa/FaPlus';
import { FaCheckCircle } from '@react-icons/all-files/fa/FaCheckCircle';
import { FaTimesCircle } from '@react-icons/all-files/fa/FaTimesCircle';
import Breadcrumb    from '../../../../commons/Breadcrumb';
import PageHeader    from '../../../../commons/PageHeader';
import PageAlert, { usePageAlert } from '../../../../commons/PageAlert';
import Confirm, { useConfirm }     from '../../../../commons/Confirm';
import { EditButton, DeleteButton, RenderActions } from '../../../../commons/DataGridActions';
import ProfaseDataGrid from '../../../../commons/Datagrid';
import { useAppDispatch, useAppSelector } from '../../../../store';
import { getEntities, deleteEntity, reset } from '../../../../manager/usuario.reducer';

const BASE = '/configuracion/usuarios/usuarios';

const boolCell = (params: any) =>
  params.value
    ? <FaCheckCircle style={{ color: '#16a34a', fontSize: 15 }} />
    : <FaTimesCircle style={{ color: '#dc2626', fontSize: 15 }} />;

export default function UsuarioPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const topRef   = useRef<HTMLDivElement>(null);

  const entities      = useAppSelector(s => s.usuario.entities);
  const totalItems    = useAppSelector(s => s.usuario.totalItems);
  const loading       = useAppSelector(s => s.usuario.loading);
  const updating      = useAppSelector(s => s.usuario.updating);
  const updateSuccess = useAppSelector(s => s.usuario.updateSuccess);

  const [rows, setRows]             = useState<any[]>([]);
  const [lastParams, setLastParams] = useState<any>(null);

  const { alert, showToast }                               = usePageAlert();
  const { confirmConfig, ask, dismiss, setConfirmLoading } = useConfirm();

  useEffect(() => {
    const msg = (location.state as any)?.successMsg;
    if (msg) { showToast('success', msg); window.history.replaceState({}, ''); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (updateSuccess) {
      dispatch(reset());
      dismiss();
      if (lastParams) dispatch(getEntities(lastParams));
      showToast('success', 'Usuario eliminado correctamente.');
    }
  }, [updateSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setConfirmLoading(updating); }, [updating]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setRows(entities ?? []); }, [entities]);

  const fetchData = (params: any) => {
    setLastParams(params);
    dispatch(getEntities(params));
  };

  const renderActions = (params: any) => (
    <RenderActions>
      <EditButton onClick={() => navigate(`${BASE}/${params.row.id}/edit`)} />
      <DeleteButton onClick={() => {
        ask(
          <>¿Confirmas que deseas eliminar al usuario <strong>{params.row.usuario}</strong>? Esta acción no se puede deshacer.</>,
          () => dispatch(deleteEntity(params.row.id)),
          { title: 'Eliminar Usuario', confirmLabel: 'Eliminar', variant: 'danger' },
        );
        topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }} />
    </RenderActions>
  );

  const cols = [
    { field: 'id',        headerName: 'ID',        flex: 0.4 },
    { field: 'usuario',   headerName: 'Usuario',   flex: 1.2 },
    { field: 'nombre',    headerName: 'Nombre',    flex: 1.2 },
    { field: 'apellido',  headerName: 'Apellido',  flex: 1.2 },
    { field: 'nombreCompleto',    headerName: 'Nombre Completo',    flex: 1.2 },
    { field: 'email',     headerName: 'Email',     flex: 1.8 },
    { field: 'habilitado', headerName: 'Habilitado', flex: 0.7, renderCell: boolCell },
    { field: '__actions', headerName: '',           flex: 0.8, renderCell: renderActions },
  ];

  const filters = [
    { label: 'Usuario',  name: 'usuario' },
    { label: 'Nombre',   name: 'nombre' },
    { label: 'Apellido', name: 'apellido' },
    { label: 'Nombre Completo', name: 'nombreCompleto' },
     { label: 'Email',    name: 'email' },
  ];

  return (
    <div style={g.page} ref={topRef}>
      <Breadcrumb items={[
        { label: 'Inicio',        to: '/',                          icon: <FaHome /> },
        { label: 'Configuración', to: '/configuracion',             icon: <FaCog /> },
        { label: 'Usuarios',      to: '/configuracion/usuarios',    icon: <FaUsers /> },
        { label: 'Usuarios',                                        icon: <FaUserCog /> },
      ]} />

      <PageHeader
        icon={<FaUserCog />}
        iconColor="#0ea5e9"
        title="Usuarios"
        subtitle="Gestión de usuarios del sistema"
        action={{
          label: <><FaPlus style={{ marginRight: 6 }} />Nuevo Usuario</>,
          onClick: () => navigate(`${BASE}/new`),
          color: '#0ea5e9',
        }}
      />

      <Confirm config={confirmConfig} />
      {alert && <PageAlert alert={alert} />}

      <ProfaseDataGrid
        cols={cols}
        title=""
        filters={filters}
        fetch={fetchData}
        rowCount={totalItems}
        loading={loading}
        rows={rows}
      />
    </div>
  );
}

const g: Record<string, React.CSSProperties> = {
  page: { display: 'flex', flexDirection: 'column', gap: 24 },
};
